import * as React from "react";
import { useMutation } from "@tanstack/react-query";
import { AlertCircle, ExternalLink, Loader2, ShieldAlert } from "lucide-react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Textarea,
} from "@eduviaos/ui-kit";
import { ApiError } from "@eduviaos/shared";
import { api } from "@/lib/api";

/**
 * Phase SC-22 — "Open centre admin". The SA states why they are entering
 * the centre; the BE mints a single-use, 60-second code and returns the IA
 * URL with the code in the fragment. A new tab then signs in to that
 * centre's admin portal with owner access, without touching the SA's own
 * session in this tab.
 */

export interface SupportSessionStartResponse {
  code: string;
  ia_url: string;
  expires_in: number;
  support_session_id: string;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tenantSlug: string;
  tenantName: string;
}

const REASON_MIN = 5;
const REASON_MAX = 200;

export function OpenCentreAdminDialog({ open, onOpenChange, tenantSlug, tenantName }: Props) {
  const [reason, setReason] = React.useState("");
  const [blockedUrl, setBlockedUrl] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (open) {
      setReason("");
      setBlockedUrl(null);
    }
  }, [open]);

  const startMutation = useMutation<
    SupportSessionStartResponse,
    Error,
    { reason: string; tab: Window | null }
  >({
    mutationFn: ({ reason: value }) =>
      api.post<SupportSessionStartResponse>(
        `/super-admin/tenants/${encodeURIComponent(tenantSlug)}/support-session`,
        { reason: value },
      ),
    onSuccess: (data, { tab }) => {
      if (tab && !tab.closed) {
        tab.location.replace(data.ia_url);
        onOpenChange(false);
        return;
      }
      setBlockedUrl(data.ia_url);
    },
    onError: (_err, { tab }) => {
      tab?.close();
    },
  });

  const trimmed = reason.trim();
  const valid = trimmed.length >= REASON_MIN && trimmed.length <= REASON_MAX;

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid || startMutation.isPending) return;
    // Open the tab inside the click so popup blockers allow it, then cut
    // the opener link before the IA URL (and its code) loads.
    const tab = window.open("about:blank", "_blank");
    if (tab) tab.opener = null;
    startMutation.mutate({ reason: trimmed, tab });
  };

  const errorMsg = startMutation.error ? describeError(startMutation.error) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Open centre admin · {tenantName}</DialogTitle>
          <DialogDescription>
            Opens this centre&apos;s admin portal in a new tab with full owner access for 60
            minutes. The centre&apos;s audit log records you and the reason below on every change.
          </DialogDescription>
        </DialogHeader>

        {blockedUrl ? (
          <div className="grid gap-3">
            <div
              role="status"
              className="border-warning/40 bg-warning/10 text-foreground flex items-start gap-2 rounded-md border px-3 py-2 text-sm"
            >
              <ShieldAlert className="text-warning mt-0.5 size-4 shrink-0" aria-hidden="true" />
              <span>
                Your browser blocked the new tab. Open it from the link below within a minute — the
                link works once.
              </span>
            </div>
            <Button asChild className="w-fit">
              <a
                href={blockedUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => onOpenChange(false)}
              >
                <ExternalLink className="size-4" aria-hidden="true" />
                Open {tenantName}
              </a>
            </Button>
          </div>
        ) : (
          <form id="open-centre-admin-form" onSubmit={onSubmit} className="grid gap-1.5">
            <Label htmlFor="support-reason">Reason</Label>
            <Textarea
              id="support-reason"
              required
              autoFocus
              rows={3}
              minLength={REASON_MIN}
              maxLength={REASON_MAX}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Centre asked for help setting up fee plans"
              aria-describedby="support-reason-hint"
            />
            <p id="support-reason-hint" className="text-muted-foreground text-xs tabular-nums">
              Shown to the centre in its audit log. {trimmed.length}/{REASON_MAX}
            </p>
            {errorMsg && (
              <div
                role="alert"
                className="border-destructive/50 bg-destructive/5 text-destructive mt-1 flex items-start gap-2 rounded-md border px-3 py-2 text-sm"
              >
                <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                <span>{errorMsg}</span>
              </div>
            )}
          </form>
        )}

        <DialogFooter>
          {blockedUrl ? (
            <Button variant="ghost" type="button" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          ) : (
            <>
              <Button variant="ghost" type="button" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                form="open-centre-admin-form"
                disabled={!valid || startMutation.isPending}
              >
                {startMutation.isPending ? (
                  <>
                    <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                    Opening…
                  </>
                ) : (
                  <>
                    <ExternalLink className="size-4" aria-hidden="true" />
                    Open centre admin
                  </>
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function OpenCentreAdminButton({
  tenantSlug,
  tenantName,
}: {
  tenantSlug: string;
  tenantName: string;
}) {
  const [open, setOpen] = React.useState(false);
  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
        <ExternalLink className="size-3.5" aria-hidden="true" />
        Open centre admin
      </Button>
      <OpenCentreAdminDialog
        open={open}
        onOpenChange={setOpen}
        tenantSlug={tenantSlug}
        tenantName={tenantName}
      />
    </>
  );
}

function describeError(err: Error): string {
  if (!(err instanceof ApiError)) {
    return err.message || "Could not open the centre admin. Try again.";
  }
  switch (err.code) {
    case "RATE_LIMITED":
      return "You've opened too many support sessions in the last minute. Wait a moment and retry.";
    case "NOT_FOUND":
      return "This centre no longer exists. Refresh the list.";
    case "FORBIDDEN":
      return "Only super admins can open a centre's admin portal.";
    case "UNAUTHORIZED":
      return "Your session expired. Sign in again and retry.";
    case "VALIDATION_ERROR":
      return `Give a reason between ${REASON_MIN} and ${REASON_MAX} characters.`;
    default:
      return err.message || "Could not open the centre admin. Try again.";
  }
}
